import React, { useContext, useState } from "react";
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
// import { LineChart } from "@mui/x-charts/LineChart";
import { Icon } from "@iconify/react";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import TableRowsRoundedIcon from "@mui/icons-material/TableRowsRounded";
import CardTitle from "./CardTitle";
import { AuthContext } from "../../context/AuthContextProvider";
import { RequireAuth } from "../../RequireAuth";
import {
  Area,
  CartesianGrid,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  LineChart,
} from "recharts";

const DashboardTestsLine = ({
  selectedDateFilter,
  granularityState,
  setGranularityState,
  loading,
  lineData,
  isLineAllZero,
  barChartLoading,
  isLoadingTimeseries,
  // Plot height. Defaults to the original 350 so existing callers are unchanged;
  // the b2b dashboard passes a shorter value so this card lines up with the
  // narrower cards beside it instead of stretching the whole row.
  chartHeight = 350,
}) => {
  const { user } = useContext(AuthContext);

  // chart <-> table view toggle for the tests data
  const [view, setView] = useState("chart");

  const LINE_META = [
    { key: "total", label: "Total Tests", color: "#5751E9" },
    { key: "normal", label: "Normal", color: "#53B253" },
    { key: "abnormal", label: "Abnormal", color: "#FFC46C" },
    { key: "critical", label: "Critical", color: "#FB8588" },
    { key: "retake", label: "Retake", color: "#C2C9FF" },
    { key: "beyond_critical", label: "Beyond Critical", color: "#CCCCCC" },
  ];

  // Reusable chart/table toggle (no export for this card)
  const renderControls = () => (
    <ToggleButtonGroup
      value={view}
      exclusive
      size="small"
      onChange={(e, val) => val && setView(val)}
      sx={{
        backgroundColor: "#F4F5F7",
        borderRadius: "10px",
        p: "3px",
        "& .MuiToggleButton-root": {
          border: "none",
          borderRadius: "8px !important",
          px: 1.25,
          py: 0.5,
          color: "var(--app-text-muted)",
          textTransform: "none",
          gap: "6px",
          "&.Mui-selected": {
            backgroundColor: "var(--app-surface)",
            color: "#5751E9",
            boxShadow: "var(--app-shadow)",
            "&:hover": { backgroundColor: "var(--app-surface)" },
          },
        },
      }}
    >
      <ToggleButton value="chart" aria-label="chart view">
        <ShowChartRoundedIcon sx={{ fontSize: 18 }} />
      </ToggleButton>
      <ToggleButton value="table" aria-label="table view">
        <TableRowsRoundedIcon sx={{ fontSize: 18 }} />
      </ToggleButton>
    </ToggleButtonGroup>
  );

  // Series legend. Rendered BELOW the plot so the chart keeps the top of the
  // card — the band under the x-axis is space the legend can share.
  const renderLegend = (paddingLeft = 1) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        // Keys are sized to fit all six series on a single line at desktop
        // widths — a wrapped second row costs the card ~34px of height and
        // leaves every card in the row stretched to match.
        flexWrap: "wrap",
        gap: 1.25,
        rowGap: 1,
        overflowX: { xs: "auto", md: "visible" },
        pb: { xs: 1, md: 0 },
        paddingLeft,
      }}
    >
      {LINE_META.map((item) => (
        <Box
          key={item.label}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            whiteSpace: "nowrap",
          }}
        >
          <Box
            sx={{
              width: 16,
              height: 5,
              borderRadius: 8,
              backgroundColor: item.color,
              flexShrink: 0,
            }}
          />
          <Typography
            variant="body2"
            sx={{
              fontSize: "12px",
              color: "var(--app-text)",
              fontWeight: 600,
            }}
          >
            {item.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  // Table view of the same tests data shown in the line chart
  const renderTable = () => (
    <TableContainer
      sx={{
        maxHeight: 350,
        borderRadius: "12px",
        border: "1px solid var(--app-border)",
        mt: 2,
      }}
    >
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                fontWeight: 700,
                color: "var(--app-text)",
                backgroundColor: "var(--app-surface-2)",
                fontFamily: "Figtree",
              }}
            >
              Period
            </TableCell>
            {LINE_META.map((s) => (
              <TableCell
                key={s.key}
                align="center"
                sx={{
                  fontWeight: 700,
                  color: "var(--app-text)",
                  backgroundColor: "var(--app-surface-2)",
                  fontFamily: "Figtree",
                  whiteSpace: "nowrap",
                }}
              >
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: s.color,
                    }}
                  />
                  {s.label}
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {lineData.map((row, idx) => (
            <TableRow key={`${row.x}-${idx}`} hover>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: "var(--app-text)",
                  fontFamily: "Figtree",
                }}
              >
                {row.x}
              </TableCell>
              {LINE_META.map((s) => (
                <TableCell
                  key={s.key}
                  align="center"
                  sx={{
                    fontWeight: s.key === "total" ? 800 : 400,
                    color:
                      s.key === "total" ? "var(--app-text)" : "var(--app-text)",
                    fontFamily: "Figtree",
                  }}
                >
                  {row[s.key] ?? 0}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const BAR_COLORS = {
    total: "#5751E9",
    normal: "#53B253",
    abnormal: "#FFC46C",
    critical: "#FB8588",
    retake: "#C2C9FF",
    beyond_critical: "#CCCCCC",
  };
  const CustomTooltip3 = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <Box
        sx={{
          backgroundColor: "var(--app-surface)",
          px: "16px",
          py: "12px",
          borderRadius: "10px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          fontSize: "13px",
          color: "var(--app-text)",
          border: "1px solid #F3F4F6",
          minWidth: "160px",
        }}
      >
        <Box
          sx={{
            fontWeight: 600,
            fontSize: "14px",
            mb: "6px",
          }}
        >
          {label}
        </Box>

        <Box
          sx={{
            borderBottom: "1px solid var(--app-border)",
            my: "6px",
            mb: "8px",
          }}
        />

        <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          {payload.map((entry, idx) => {
            const color = BAR_COLORS[entry.dataKey];

            return (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "8px",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <Box
                    component="span"
                    sx={{
                      width: 10,
                      height: 10,
                      backgroundColor: color,
                      borderRadius: "50%",
                      display: "inline-block",
                    }}
                  />
                  <Typography sx={{ fontSize: 13 }}>
                    {entry.name?.charAt(0).toUpperCase() + entry.name?.slice(1)}
                  </Typography>
                </Box>

                <Box component="strong" sx={{ fontSize: 13 }}>
                  {entry.value ?? "-"}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  return (
    <>
      {user?.userType === "b2b-doctor" ? (
        <>
          <RequireAuth role={["b2b-doctor"]} permission={"canInterpreteReport"}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "8px",
                paddingLeft: 3,
              }}
            >
              <CardTitle>Tests</CardTitle>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: ".875rem",
                  fontWeight: 500,
                  color: "text.secondary",
                }}
              >
                {selectedDateFilter === "last_30_days" ||
                selectedDateFilter === "last_3_months" ? (
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={granularityState.value}
                      onChange={(e) =>
                        setGranularityState((prev) => ({
                          ...prev,
                          value: e.target.value,
                          label:
                            granularityState.options.find(
                              (o) => o.value === e.target.value,
                            )?.label || prev.label,
                        }))
                      }
                      sx={{ fontSize: ".875rem", fontWeight: 500 }}
                    >
                      {granularityState.options.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {selectedDateFilter === "custom"
                        ? `Custom – ${granularityState.label}`
                        : `${granularityState.label}`}
                    </Typography>
                    <Icon
                      icon="mdi:chevron-down"
                      width="16"
                      className="text-gray-500"
                    />
                  </>
                )}
                {renderControls()}
              </Box>
            </Box>
            <Box
              className="col-md-12 pt-20"
              sx={{
                borderRight: "1px solid var(--app-border)",
                pl: 0, // paddingLeft: 0px
                pr: 0, // paddingRight: 0px
                pt: "20px", // equivalent to pt-20
              }}
            >
              <Box sx={{ px: { xs: 1.5, md: 0 } }}>
                {Array.isArray(lineData) &&
                lineData.length > 0 &&
                !isLineAllZero ? (
                  view === "table" ? (
                    renderTable()
                  ) : (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {/* Chart */}
                      <Box
                        sx={{
                          width: "100%",
                          height: chartHeight,
                          overflowX: { xs: "auto", md: "hidden" },
                        }}
                      >
                        <Box
                          sx={{ minWidth: { xs: 700, sm: 850, md: "100%" } }}
                        >
                          <ResponsiveContainer width="100%" height={chartHeight}>
                            <LineChart
                              data={lineData}
                              margin={{
                                top: 30,
                                right: 20,
                                left: 0,
                                bottom: 20,
                              }}
                            >
                              <defs>
                                <linearGradient
                                  id="colorTotal"
                                  x1="0"
                                  y1="0"
                                  x2="0"
                                  y2="1"
                                >
                                  <stop
                                    offset="0%"
                                    stopColor="#5751E9"
                                    stopOpacity={0.2}
                                  />
                                  <stop
                                    offset="100%"
                                    stopColor="#5751E9"
                                    stopOpacity={0}
                                  />
                                </linearGradient>
                              </defs>

                              <CartesianGrid
                                stroke="var(--app-border)"
                                vertical={false}
                              />

                              <XAxis
                                dataKey="x"
                                tick={{
                                  fontSize: 13,
                                  fill: "var(--app-text)",
                                  fontWeight: 700,
                                }}
                                axisLine={false}
                                tickLine={false}
                              />

                              <YAxis
                                tick={{
                                  fontSize: 14,
                                  fill: "var(--app-text)",
                                  fontWeight: 500,
                                }}
                                axisLine={false}
                                tickLine={false}
                                width={30}
                              />

                              <Tooltip
                                content={<CustomTooltip3 />}
                                cursor={{ stroke: "#ccc", strokeWidth: 1 }}
                              />

                              {/* Area for Total */}
                              <Area
                                type="monotone"
                                dataKey="total"
                                stroke="#5751E9"
                                fill="url(#colorTotal)"
                                strokeWidth={2}
                              />

                              {/* Lines */}
                              <Line
                                type="monotone"
                                dataKey="total"
                                stroke="#5751E9"
                                strokeWidth={2}
                                dot={false}
                                activeDot={{
                                  r: 4,
                                  strokeWidth: 2,
                                  stroke: "var(--app-surface)",
                                }}
                              />
                              <Line
                                type="monotone"
                                dataKey="normal"
                                stroke="#53B253"
                                strokeWidth={2}
                                dot={false}
                                activeDot={{
                                  r: 4,
                                  strokeWidth: 2,
                                  stroke: "var(--app-surface)",
                                }}
                              />
                              <Line
                                type="monotone"
                                dataKey="abnormal"
                                stroke="#FFC46C"
                                strokeWidth={2}
                                dot={false}
                                activeDot={{
                                  r: 4,
                                  strokeWidth: 2,
                                  stroke: "var(--app-surface)",
                                }}
                              />
                              <Line
                                type="monotone"
                                dataKey="critical"
                                stroke="#FB8588"
                                strokeWidth={2}
                                dot={false}
                                activeDot={{
                                  r: 4,
                                  strokeWidth: 2,
                                  stroke: "var(--app-surface)",
                                }}
                              />
                              <Line
                                type="monotone"
                                dataKey="retake"
                                stroke="#C2C9FF"
                                strokeWidth={2}
                                dot={false}
                                activeDot={{
                                  r: 4,
                                  strokeWidth: 2,
                                  stroke: "var(--app-surface)",
                                }}
                              />
                              <Line
                                type="monotone"
                                dataKey="beyond_critical"
                                name="Beyond Critical"
                                stroke="#CCCCCC"
                                strokeWidth={2}
                                dot={false}
                                activeDot={{
                                  r: 4,
                                  strokeWidth: 2,
                                  stroke: "var(--app-surface)",
                                }}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </Box>
                      </Box>

                      {renderLegend()}
                    </Box>
                  )
                ) : (
                  <Box
                    sx={{
                      height: chartHeight,
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={barChartLoading}
                      alt="No data"
                      width={130}
                      height={130}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        fontSize: 16,
                        color: "var(--app-text-muted)",
                        mt: 1,
                        fontFamily: "Figtree",
                      }}
                    >
                      No Tests Taken
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </RequireAuth>
        </>
      ) : (
        <>
          {/* Tests (Line) */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "8px",
              paddingLeft: 3,
            }}
          >
            <CardTitle>Tests</CardTitle>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: ".875rem",
                fontWeight: 500,
                color: "text.secondary",
              }}
            >
              {selectedDateFilter === "last_30_days" ||
              selectedDateFilter === "last_3_months" ? (
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={granularityState.value}
                    onChange={(e) =>
                      setGranularityState((prev) => ({
                        ...prev,
                        value: e.target.value,
                        label:
                          granularityState.options.find(
                            (o) => o.value === e.target.value,
                          )?.label || prev.label,
                      }))
                    }
                    sx={{ fontSize: ".875rem", fontWeight: 500 }}
                  >
                    {granularityState.options.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {selectedDateFilter === "custom"
                      ? `Custom – ${granularityState.label}`
                      : `${granularityState.label}`}
                  </Typography>
                  <Icon icon="mdi:chevron-down" width="16" />
                </>
              )}
              {renderControls()}
            </Box>
          </Box>
          <Box
            className="col-md-12 pt-20"
            sx={{
              borderRight: "1px solid var(--app-border)",
              pl: 0, // paddingLeft: 0px
              pr: 0, // paddingRight: 0px
              pt: "20px", // equivalent to pt-20
            }}
          >
            {/* Header */}

            {/* Content */}
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: chartHeight, width: "100%" }}>
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  style={{ borderRadius: 12, marginTop: 12 }}
                  height={chartHeight}
                />
              </Box>
            ) : lineData && lineData.length > 0 && !isLineAllZero ? (
              view === "table" ? (
                renderTable()
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {/* Chart */}
                  <Box
                    sx={{
                      width: "100%",
                      height: chartHeight,
                      overflowX: { xs: "auto", md: "hidden" },
                    }}
                  >
                    <Box sx={{ minWidth: { xs: 700, sm: 850, md: "100%" } }}>
                      <ResponsiveContainer width="100%" height={chartHeight}>
                        <LineChart
                          data={lineData}
                          margin={{ top: 30, right: 20, left: 0, bottom: 20 }}
                        >
                          <defs>
                            <linearGradient
                              id="colorTotal"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="0%"
                                stopColor="#5751E9"
                                stopOpacity={0.2}
                              />
                              <stop
                                offset="100%"
                                stopColor="#5751E9"
                                stopOpacity={0}
                              />
                            </linearGradient>
                          </defs>

                          <CartesianGrid
                            stroke="var(--app-border)"
                            vertical={false}
                          />

                          <XAxis
                            dataKey="x"
                            tick={{
                              fontSize: 13,
                              fill: "var(--app-text)",
                              fontWeight: 700,
                            }}
                            axisLine={false}
                            tickLine={false}
                          />

                          <YAxis
                            tick={{
                              fontSize: 14,
                              fill: "var(--app-text)",
                              fontWeight: 500,
                            }}
                            axisLine={false}
                            tickLine={false}
                            width={30}
                          />

                          <Tooltip
                            content={<CustomTooltip3 />}
                            cursor={{ stroke: "#ccc", strokeWidth: 1 }}
                          />

                          {/* Area for Total */}
                          <Area
                            type="monotone"
                            dataKey="total"
                            stroke="#5751E9"
                            fill="url(#colorTotal)"
                            strokeWidth={2}
                          />

                          {/* Lines */}
                          <Line
                            type="monotone"
                            dataKey="total"
                            stroke="#5751E9"
                            strokeWidth={2}
                            dot={false}
                            activeDot={{
                              r: 4,
                              strokeWidth: 2,
                              stroke: "var(--app-surface)",
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="normal"
                            stroke="#53B253"
                            strokeWidth={2}
                            dot={false}
                            activeDot={{
                              r: 4,
                              strokeWidth: 2,
                              stroke: "var(--app-surface)",
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="abnormal"
                            stroke="#FFC46C"
                            strokeWidth={2}
                            dot={false}
                            activeDot={{
                              r: 4,
                              strokeWidth: 2,
                              stroke: "var(--app-surface)",
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="critical"
                            stroke="#FB8588"
                            strokeWidth={2}
                            dot={false}
                            activeDot={{
                              r: 4,
                              strokeWidth: 2,
                              stroke: "var(--app-surface)",
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="retake"
                            stroke="#C2C9FF"
                            strokeWidth={2}
                            dot={false}
                            activeDot={{
                              r: 4,
                              strokeWidth: 2,
                              stroke: "var(--app-surface)",
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="beyond_critical"
                            name="Beyond Critical"
                            stroke="#CCCCCC"
                            strokeWidth={2}
                            dot={false}
                            activeDot={{
                              r: 4,
                              strokeWidth: 2,
                              stroke: "var(--app-surface)",
                            }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>

                  {renderLegend()}
                </Box>
              )
            ) : (
              <Box
                sx={{
                  height: chartHeight,
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={barChartLoading}
                  alt="No data"
                  width={130}
                  height={130}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: 16,
                    color: "var(--app-text-muted)",
                    mt: 1,
                    fontFamily: "Figtree",
                  }}
                >
                  No Tests Taken
                </Typography>
              </Box>
            )}
          </Box>
        </>
      )}
    </>
  );
};
export default DashboardTestsLine;
